import React, { useState, useRef, useEffect } from 'react';
import { FiCpu, FiX, FiZap, FiRefreshCw, FiCheck, FiAlertCircle, FiInfo, FiSend, FiMessageSquare, FiShield } from 'react-icons/fi';
import {
  suggestArchitecture,
  refineArchitecture,
  discussArchitecture,
  type ArchitectureRequirements,
  type SuggestArchitectureResult,
} from '../../api/ai';
import { createVariable } from '../../api/variables';

// ── Validation locale ──────────────────────────────────────────────────────────
interface ValidationIssue { severity: 'error' | 'warning' | 'info'; message: string; }
interface ValidationResult { score: number; issues: ValidationIssue[]; }

const AZURE_RULES = [
  { id: 'rg',  severity: 'error'   as const, check: (n: any[]) => !n.some(x => x.data?.type === 'azurerm_resource_group'),     message: 'Aucun Resource Group défini — requis pour Azure' },
  { id: 'kv',  severity: 'warning' as const, onlyEnv: 'production', check: (n: any[]) => !n.some(x => x.data?.type === 'azurerm_key_vault'), message: 'Production sans Key Vault — recommandé pour la gestion des secrets' },
  { id: 'nsg', severity: 'warning' as const, check: (n: any[]) => n.some(x => x.data?.type === 'azurerm_virtual_network') && !n.some(x => x.data?.type === 'azurerm_network_security_group'), message: 'VNet présent sans Network Security Group' },
  { id: 'mon', severity: 'warning' as const, onlyEnv: 'production', check: (n: any[]) => !n.some(x => x.data?.type === 'azurerm_application_insights' || x.data?.type === 'azurerm_log_analytics_workspace'), message: 'Production sans monitoring (Application Insights ou Log Analytics)' },
  { id: 'lrs', severity: 'info'    as const, onlyEnv: 'production', check: (n: any[]) => n.filter(x => x.data?.type === 'azurerm_storage_account').some(x => (x.data?.params?.account_replication_type || 'LRS') === 'LRS'), message: 'Storage Account(s) avec LRS — envisager GRS/ZRS pour la résilience' },
];
const AWS_RULES = [
  { id: 'vpc', severity: 'warning' as const, check: (n: any[]) => !n.some(x => x.data?.type === 'aws_vpc'), message: 'Aucun VPC défini' },
  { id: 'cw',  severity: 'warning' as const, onlyEnv: 'production', check: (n: any[]) => !n.some(x => x.data?.type === 'aws_cloudwatch_log_group'), message: 'Production sans CloudWatch' },
];
const GCP_RULES = [
  { id: 'net', severity: 'warning' as const, check: (n: any[]) => !n.some(x => x.data?.type === 'google_compute_network'), message: 'Aucun réseau VPC défini' },
];
const FABRIC_RULES = [
  { id: 'cap', severity: 'error'   as const, check: (n: any[]) => n.length > 0 && !n.some(x => x.data?.type === 'fabric_capacity'),  message: 'Aucune Fabric Capacity définie — requise pour héberger les workspaces' },
  { id: 'ws',  severity: 'error'   as const, check: (n: any[]) => n.length > 0 && !n.some(x => x.data?.type === 'fabric_workspace'),  message: 'Aucun Fabric Workspace défini — requis pour héberger les ressources' },
  { id: 'lh',  severity: 'warning' as const, check: (n: any[]) => !n.some(x => x.data?.type === 'fabric_lakehouse' || x.data?.type === 'fabric_warehouse'), message: 'Aucun stockage de données défini — Lakehouse ou Warehouse recommandé' },
];
const RULES: Record<string, typeof AZURE_RULES> = { azure: AZURE_RULES, aws: AWS_RULES as any, gcp: GCP_RULES as any, fabric: FABRIC_RULES as any };

function validateLocally(nodes: any[], provider: string, environment: string): ValidationResult {
  const rules = RULES[provider] || [];
  const issues: ValidationIssue[] = [];
  for (const rule of rules) {
    if ((rule as any).onlyEnv && (rule as any).onlyEnv !== environment) continue;
    if (rule.check(nodes)) issues.push({ severity: rule.severity, message: rule.message });
  }
  const penalties: Record<string, number> = { error: 20, warning: 10, info: 2 };
  const score = Math.max(0, 100 - issues.reduce((s, i) => s + (penalties[i.severity] || 0), 0));
  return { score, issues };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (diagramData: { nodes: any[]; edges: any[] }) => void;
  architectureId?: string;
  initialDiagram?: { nodes: any[]; edges: any[] };
}

const PROVIDERS = [
  { value: 'azure',  label: 'Azure',   flag: '🔵' },
  { value: 'aws',    label: 'AWS',     flag: '🟠' },
  { value: 'gcp',    label: 'GCP',     flag: '🔴' },
  { value: 'fabric', label: 'Fabric',  flag: '🟢' },
];

const ENVIRONMENTS = [
  { value: 'dev',        label: 'Dev' },
  { value: 'staging',    label: 'Staging' },
  { value: 'production', label: 'Production' },
];

type Phase = 'discuss' | 'diagram';
type ChatMessage = { role: 'user' | 'assistant'; text: string; nodeCount?: number };

const GREETING = `Bonjour ! Décrivez votre besoin d'infrastructure — type d'application, volumes attendus, contraintes de sécurité, régions souhaitées — et je vous proposerai une architecture adaptée avec des estimations de coût.`;

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  const bg    = score >= 80 ? '#ecfdf5' : score >= 60 ? '#fffbeb' : '#fef2f2';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: bg, border: `1px solid ${color}`, borderRadius: 10, padding: '4px 10px' }}>
      <FiShield size={13} style={{ color }} />
      <span style={{ color, fontWeight: 700, fontSize: 13 }}>{score}/100</span>
    </div>
  );
}

function IssueIcon({ severity }: { severity: string }) {
  if (severity === 'error')   return <FiAlertCircle size={13} className="text-red-500 flex-shrink-0 mt-0.5" />;
  if (severity === 'warning') return <FiAlertCircle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />;
  return <FiInfo size={13} className="text-blue-400 flex-shrink-0 mt-0.5" />;
}

function AssistantAvatar() {
  return (
    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
      <FiCpu size={11} className="text-white" />
    </div>
  );
}

const AIArchitectureGenerator: React.FC<Props> = ({ isOpen, onClose, onApply, architectureId, initialDiagram }) => {
  const [phase, setPhase]               = useState<Phase>('discuss');
  const [provider, setProvider]         = useState<'azure' | 'aws' | 'gcp' | 'fabric'>('azure');
  const [environment, setEnvironment]   = useState('production');
  const [budget, setBudget]             = useState('');

  const [loading, setLoading]           = useState(false);
  const [result, setResult]             = useState<SuggestArchitectureResult | null>(null);
  const [error, setError]               = useState<string | null>(null);
  const [validation, setValidation]     = useState<ValidationResult | null>(null);

  // Phase discuss — conversation pre-génération
  const [discussMessages, setDiscussMessages] = useState<ChatMessage[]>([{ role: 'assistant', text: GREETING }]);
  const [discussHistory, setDiscussHistory]   = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [discussInput, setDiscussInput]       = useState('');
  const [discussLoading, setDiscussLoading]   = useState(false);
  const [canGenerate, setCanGenerate]         = useState(false);

  // Phase diagram — raffinement post-génération
  const [chatMessages, setChatMessages]               = useState<ChatMessage[]>([]);
  const [conversationHistory, setConversationHistory] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [chatInput, setChatInput]                     = useState('');
  const [chatLoading, setChatLoading]                 = useState(false);
  const [currentDiagram, setCurrentDiagram]           = useState<{ nodes: any[]; edges: any[] } | null>(null);

  const discussEndRef     = useRef<HTMLDivElement>(null);
  const chatEndRef        = useRef<HTMLDivElement>(null);
  const prevDiagramKeyRef = useRef<string | null>(null);

  useEffect(() => { discussEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [discussMessages]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // Chargement initialDiagram — seulement si le diagramme a changé
  useEffect(() => {
    if (!isOpen) return;
    setError(null);

    if (initialDiagram && initialDiagram.nodes.length > 0) {
      const key = initialDiagram.nodes.map((n: any) => n.id).join(',');
      if (key !== prevDiagramKeyRef.current) {
        prevDiagramKeyRef.current = key;
        const nodeTypes = initialDiagram.nodes.map((n: any) => (n.data?.type || '') as string);
        const detectedProvider: 'azure' | 'aws' | 'gcp' | 'fabric' =
          nodeTypes.some(t => t.startsWith('aws_'))     ? 'aws' :
          nodeTypes.some(t => t.startsWith('google_'))  ? 'gcp' :
          nodeTypes.some(t => t.startsWith('fabric_'))  ? 'fabric' : 'azure';
        setProvider(detectedProvider);
        setCurrentDiagram(initialDiagram);
        setResult({
          diagramData: initialDiagram,
          metadata: { model: 'existing', tokensUsed: 0, costUsd: 0, durationMs: 0, nodeCount: initialDiagram.nodes.length, edgeCount: initialDiagram.edges.length },
        });
        setPhase('diagram');
        setChatMessages([{
          role: 'assistant',
          text: `Architecture existante chargée — ${initialDiagram.nodes.length} ressource${initialDiagram.nodes.length > 1 ? 's' : ''}. Décrivez les modifications ou améliorations souhaitées.`,
        }]);
        setConversationHistory([]);
        setValidation(validateLocally(initialDiagram.nodes, detectedProvider, environment));
      }
      // même diagramme → conservation de la conversation
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const runValidation = (diagram: { nodes: any[]; edges: any[] }) => {
    setValidation(validateLocally(diagram.nodes, provider, environment));
  };

  // Envoyer un message dans la phase de discussion
  const handleDiscuss = async () => {
    if (!discussInput.trim() || discussLoading) return;
    const msg = discussInput.trim();
    setDiscussInput('');
    setDiscussLoading(true);
    setDiscussMessages(prev => [...prev, { role: 'user', text: msg }]);

    try {
      const res = await discussArchitecture({
        message: msg,
        conversationHistory: discussHistory,
        provider,
        budget: budget ? parseInt(budget) : undefined,
        environment,
      });
      setDiscussHistory(res.conversationHistory);
      setDiscussMessages(prev => [...prev, { role: 'assistant', text: res.reply }]);
      setCanGenerate(true);
    } catch (e: any) {
      setDiscussMessages(prev => [...prev, {
        role: 'assistant',
        text: `Erreur : ${e.response?.data?.error || e.message}`,
      }]);
    } finally {
      setDiscussLoading(false);
    }
  };

  // Générer le diagramme depuis la conversation
  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const description = discussHistory
        .filter(m => m.role === 'user')
        .map(m => m.content)
        .join(' ') || 'Architecture cloud';

      const requirements: ArchitectureRequirements = {
        description,
        provider,
        environment: environment as any,
        budget: budget ? parseInt(budget) : undefined,
        contextHistory: discussHistory,
      };

      const res = await suggestArchitecture(requirements);
      setResult(res);
      setCurrentDiagram(res.diagramData);
      // Mémoriser la clé pour que la réouverture du modal préserve la conversation
      prevDiagramKeyRef.current = res.diagramData.nodes.map((n: any) => n.id).join(',');
      setPhase('diagram');
      const introText = res.assistantMessage
        || `Architecture générée avec ${res.metadata.nodeCount} ressources. Décrivez les modifications souhaitées.`;
      // Inclure l'historique de discussion pour qu'il reste visible
      setChatMessages([
        ...discussMessages,
        { role: 'assistant', text: `━━━ Architecture générée — ${res.metadata.nodeCount} ressources ━━━\n\n${introText}`, nodeCount: res.metadata.nodeCount },
      ]);
      runValidation(res.diagramData);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Raffiner le diagramme via chat (phase diagram)
  const handleSendChat = async () => {
    if (!chatInput.trim() || chatLoading || !currentDiagram) return;
    const instruction = chatInput.trim();
    setChatInput('');
    setChatLoading(true);
    setChatMessages(prev => [...prev, { role: 'user', text: instruction }]);

    try {
      const res = await refineArchitecture({
        diagramData: currentDiagram,
        instruction,
        provider,
        conversationHistory,
      });

      setCurrentDiagram(res.diagramData);
      setConversationHistory(res.conversationHistory);
      setResult(prev => prev ? { ...prev, diagramData: res.diagramData, metadata: res.metadata } : null);

      let variableLines: string[] = [];
      if (architectureId && res.suggestedVariables?.length > 0) {
        for (const v of res.suggestedVariables) {
          try {
            await createVariable(architectureId, {
              name: v.name,
              displayName: v.displayName,
              description: v.description,
              scope: v.scope || 'parameter',
              type: v.type || 'string',
              defaultValue: v.defaultValue !== undefined ? { value: v.defaultValue } : undefined,
            });
            variableLines.push(`Paramètre \`${v.name}\` créé (valeur par défaut : ${v.defaultValue ?? '—'})`);
          } catch (err: any) {
            const detail = err?.response?.data?.error || err?.message || '';
            if (/already exists/i.test(detail)) {
              // Variable déjà présente — pas d'erreur à afficher
            } else {
              console.error(`[AI] createVariable failed for ${v.name}:`, detail);
              variableLines.push(`Paramètre \`${v.name}\` : erreur création (${detail || 'erreur inconnue'})`);
            }
          }
        }
      }

      const baseText = res.assistantMessage
        || `Modification appliquée — ${res.metadata.nodeCount} ressources, ${res.metadata.edgeCount} connexions.`;
      const fullText = variableLines.length > 0 ? `${baseText}\n${variableLines.join('\n')}` : baseText;
      setChatMessages(prev => [...prev, { role: 'assistant', text: fullText, nodeCount: res.metadata.nodeCount }]);
      runValidation(res.diagramData);
    } catch (e: any) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        text: `Erreur : ${e.response?.data?.error || e.message}`,
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleApply = () => {
    if (!currentDiagram) return;
    onApply(currentDiagram);
    onClose();
  };

  const handleReset = () => {
    setPhase('discuss');
    setResult(null);
    setError(null);
    setValidation(null);
    setChatMessages([]);
    setConversationHistory([]);
    setCurrentDiagram(null);
    setDiscussMessages([{ role: 'assistant', text: GREETING }]);
    setDiscussHistory([]);
    setCanGenerate(false);
    prevDiagramKeyRef.current = null;
  };

  return (
    <div
      className="fixed inset-0 items-center justify-center bg-black/50 backdrop-blur-sm"
      style={{ zIndex: 9999, display: isOpen ? 'flex' : 'none' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <FiCpu className="text-white text-lg" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Générer avec l'IA</h2>
              <p className="text-xs text-gray-500">
                {phase === 'discuss'
                  ? 'Discutez votre besoin — générez quand vous êtes prêt'
                  : 'Architecture générée — affinez par la conversation'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <FiX />
          </button>
        </div>

        {/* Barre provider / env / budget — toujours visible */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 flex-shrink-0 flex-wrap">
          <div className="flex gap-1">
            {PROVIDERS.map(p => (
              <button
                key={p.value}
                onClick={() => setProvider(p.value as any)}
                disabled={phase === 'diagram'}
                className={`flex items-center space-x-1 py-1 px-2.5 rounded-lg border text-xs font-medium transition-all ${
                  provider === p.value
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <span>{p.flag}</span><span>{p.label}</span>
              </button>
            ))}
          </div>
          <select
            value={environment}
            onChange={e => setEnvironment(e.target.value)}
            disabled={phase === 'diagram'}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:bg-gray-50"
          >
            {ENVIRONMENTS.map(env => <option key={env.value} value={env.value}>{env.label}</option>)}
          </select>
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs text-gray-400">Budget</span>
            <input
              type="number"
              value={budget}
              onChange={e => setBudget(e.target.value)}
              disabled={phase === 'diagram'}
              placeholder="€/mois"
              className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:bg-gray-50"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">

          {phase === 'discuss' ? (
            /* ── Phase Discuss : conversation avant génération ── */
            <div className="flex flex-col h-full space-y-3">

              {/* Messages */}
              <div className="flex-1 bg-gray-50 rounded-xl p-3 overflow-y-auto space-y-3 min-h-0" style={{ minHeight: 280 }}>
                {discussMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && <AssistantAvatar />}
                    <div className={`max-w-[82%] rounded-xl px-3 py-2 text-xs whitespace-pre-wrap leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700'
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {discussLoading && (
                  <div className="flex justify-start">
                    <AssistantAvatar />
                    <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-400 flex items-center space-x-1.5">
                      <FiRefreshCw className="animate-spin text-blue-500" size={11} />
                      <span>Claude réfléchit…</span>
                    </div>
                  </div>
                )}
                <div ref={discussEndRef} />
              </div>

              {/* Input */}
              <div className="flex items-end space-x-2 flex-shrink-0">
                <textarea
                  value={discussInput}
                  onChange={e => {
                    setDiscussInput(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleDiscuss();
                    }
                  }}
                  placeholder="Décrivez votre besoin, posez une question ou affinez la proposition…"
                  disabled={discussLoading}
                  rows={1}
                  className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 resize-none overflow-hidden"
                  style={{ minHeight: 40, maxHeight: 160, lineHeight: '1.5' }}
                />
                <button
                  onClick={handleDiscuss}
                  disabled={!discussInput.trim() || discussLoading}
                  className="px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  style={{ height: 40 }}
                >
                  <FiSend size={16} />
                </button>
              </div>
              <p className="text-xs text-gray-400 flex-shrink-0">
                Entrée pour envoyer • Shift+Entrée pour saut de ligne • Discutez jusqu'à trouver l'architecture idéale, puis cliquez sur <strong>Générer</strong>
              </p>

              {error && (
                <div className="flex items-start space-x-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex-shrink-0">
                  <FiAlertCircle className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          ) : (
            /* ── Phase Diagram : résultat + validation + chat raffinement ── */
            <div className="space-y-4">

              {/* Score */}
              {validation && (
                <div className="bg-gray-50 rounded-xl p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FiShield size={14} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Analyse de l'architecture</span>
                    </div>
                    <ScoreBadge score={validation.score} />
                  </div>
                  {validation.issues.length > 0 ? (
                    <div className="space-y-1.5">
                      {validation.issues.map((issue, i) => (
                        <div key={i} className="flex items-start space-x-2 text-xs">
                          <IssueIcon severity={issue.severity} />
                          <span className={
                            issue.severity === 'error' ? 'text-red-700' :
                            issue.severity === 'warning' ? 'text-amber-700' : 'text-blue-600'
                          }>{issue.message}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-green-600 flex items-center space-x-1">
                      <FiCheck size={12} /><span>Architecture conforme aux bonnes pratiques</span>
                    </p>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Ressources', value: currentDiagram ? currentDiagram.nodes.length : result?.metadata.nodeCount },
                  { label: 'Connexions',  value: currentDiagram ? currentDiagram.edges.length : result?.metadata.edgeCount },
                  { label: 'Durée',       value: result?.metadata.durationMs ? `${(result.metadata.durationMs / 1000).toFixed(1)}s` : 'Canvas' },
                ].map(stat => (
                  <div key={stat.label} className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-sm font-semibold text-gray-900">{stat.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Preview nodes */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Ressources actuelles</p>
                <div className="bg-gray-50 rounded-xl p-3 max-h-28 overflow-y-auto space-y-1.5">
                  {(currentDiagram || result?.diagramData)?.nodes.map((node: any, i: number) => (
                    <div key={i} className="flex items-center space-x-2 text-xs text-gray-600">
                      <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                      <span className="font-medium">{node.data?.label}</span>
                      <span className="text-gray-400">({node.data?.type})</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chat raffinement */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1.5">
                  <FiMessageSquare className="text-blue-500" />
                  <span>Affiner par la conversation</span>
                </p>
                <div className="bg-gray-50 rounded-xl p-3 max-h-44 overflow-y-auto space-y-3 mb-3">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.role === 'assistant' && <AssistantAvatar />}
                      <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs whitespace-pre-line leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-700'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <AssistantAvatar />
                      <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-400 flex items-center space-x-1.5">
                        <FiRefreshCw className="animate-spin text-blue-500" size={11} />
                        <span>Claude modifie l'architecture… (peut prendre jusqu'à 2 min)</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="flex items-end space-x-2">
                  <textarea
                    value={chatInput}
                    onChange={e => {
                      setChatInput(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendChat();
                      }
                    }}
                    placeholder="Ex: Ajoute un Key Vault, connecte-le au SQL Server…"
                    disabled={chatLoading}
                    rows={1}
                    className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 resize-none overflow-hidden"
                    style={{ minHeight: 40, maxHeight: 160, lineHeight: '1.5' }}
                  />
                  <button
                    onClick={handleSendChat}
                    disabled={!chatInput.trim() || chatLoading}
                    className="px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    style={{ height: 40 }}
                  >
                    <FiSend size={16} />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Entrée pour envoyer • Shift+Entrée pour saut de ligne • Ex: "Ajoute Application Insights", "Passe en multi-régions"</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0">
          {phase === 'diagram' ? (
            <>
              <button
                onClick={handleReset}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <FiRefreshCw className="text-sm" />
                <span>Recommencer</span>
              </button>
              <button
                onClick={handleApply}
                className="flex items-center space-x-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
              >
                <FiCheck />
                <span>Charger dans le Canvas</span>
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                Annuler
              </button>
              <button
                onClick={handleGenerate}
                disabled={!canGenerate || loading}
                className="flex items-center space-x-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {loading ? (
                  <><FiRefreshCw className="animate-spin" /><span>Génération en cours…</span></>
                ) : (
                  <><FiZap /><span>Générer l'architecture</span></>
                )}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default AIArchitectureGenerator;
